<template>
    <div class="mt-5 mx-auto max-w-7xl px-5">
        <div class="container mx-auto p-6">

            <div class="p-6 bg-sky-500 text-white rounded-lg mb-6">
                <h2 class="text-2xl leading-none tracking-tight lg:text-3xl text-white">{{ jobData.title }}</h2>
            </div>

            <!-- Tabbed Job Details View -->
            <div class="bg-white shadow rounded-lg p-6">
                <!-- Job Title Section -->
                <div class="flex items-center justify-between mb-6">
                    <!-- Job Info -->
                    <div class="flex flex-col leading-tight">
                        <img
                            v-if="jobData.company_logo"
                            :src="jobData.company_logo"
                            :alt="`${jobData.company_name} logo`"
                            class="mb-3 h-12 w-12 rounded-lg border border-gray-200 bg-white object-contain p-1"
                        />
                        <p class="text-gray-700">{{ jobData.company_name }}</p>
                        <p class="mt-1">{{ jobData.work_location }}</p>
                        <p class="text-sm text-gray-500 mt-2">{{ formattedPostedDate }}</p>
                        <p class="text-sm text-gray-500">Job ID: <span class="hover:cursor-pointer" @click="copyJobIdToClipboard">#{{ jobData.slug }}</span></p>
                    </div>

                    <!-- Call-to-Action Button -->
                    <div>
                        <a
                            v-if="isImported"
                            :href="jobData.external_apply_url"
                            target="_blank"
                            rel="nofollow sponsored noopener noreferrer"
                            class="inline-flex px-10 py-3 text-base font-semibold text-white bg-amber-600 rounded-full shadow hover:bg-amber-700 transition-transform transform hover:scale-105"
                        >
                            Apply on company site
                        </a>
                        <button
                            v-else-if="!hasApplied"
                            @click="handleApplyClick"
                            class="px-10 py-3 text-base font-semibold text-white bg-sky-500 rounded-full shadow hover:bg-sky-600 transition-transform transform hover:scale-105">
                            Apply Now
                        </button>
                        <p v-else class="px-10 py-3 text-base font-semibold text-gray-500 bg-gray-200 rounded-full select-none">
                            Application successful
                        </p>
                    </div>
                </div>

                <!-- Google Map Component -->
                <div v-if="jobData.location_lat && jobData.location_lng" class="mt-2 mb-4 border border-gray-300 rounded-lg overflow-hidden relative">
                    <div ref="mapContainer" class="h-64 w-full"></div>

                    <!-- Loading Overlay -->
                    <div v-if="isMapLoading" class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center">
                        <div class="flex flex-col items-center">
                            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
                            <p class="mt-2 text-sky-600 text-sm font-medium">Loading map...</p>
                        </div>
                    </div>
                </div>

                <div class="border-b border-gray-200"></div>


                
                <!-- Job Details -->
                <div class="text-smaller">
                    <div class="py-3">
                        <span class="block text-gray-500 my-2">Job description</span>
                        <div
                            v-if="isImported"
                            class="imported-job-description text-gray-700 mt-2"
                            v-html="importedDescriptionHtml"
                        ></div>
                        <p v-else class="text-gray-600 mt-2" v-html="jobData.description_rendered"></p>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
                        <!-- Work type -->
                        <div>
                            <span class="block text-gray-500">Work type</span>
                            <p class="text-gray-800">{{ formatDatabaseString(jobData.work_type) }}</p>
                        </div>
                        <!-- Contract length (optional) -->
                        <div v-if="!isImported" :class="jobData.work_type !== 'contract' ? 'invisible' : ''">
                            <span class="block text-gray-500">Length of contract</span>
                            <p class="text-gray-800">
                                {{ jobData.contract_length || 'Not applicable' }}
                            </p>
                        </div>
                        <!-- Start date -->
                        <div>
                            <span class="block text-gray-500">{{ isImported ? 'Application deadline' : 'Start date' }}</span>
                            <p class="text-gray-800">{{ isImported ? formatCalendarDate(jobData.closing_date) : formatDate(jobData.contract_start_date) }}</p>
                        </div>
                        <!-- Rate and rate type -->
                        <div>
                            <span class="block text-gray-500">Rate</span>
                            <p v-if="isImported" class="text-gray-800">{{ importedSalary }}</p>
                            <p v-else-if="jobData && jobData.rate_category && jobData.rate_category !== 'custom_rate'">
                                {{ jobData.rate_category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\s*Rate\s*/gi, '').trim() }} Rate
                            </p>
                            <p v-else class="text-gray-800">${{ jobData.rate }} / {{ jobData.rate_type }}</p>
                        </div>
                    </div>

                    <div class="mt-6">
                        <span class="block text-gray-500">Work arrangement</span>
                        <p v-if="isImported" class="text-gray-800">{{ formatDatabaseString(jobData.workplace_type) || 'Not specified' }}</p>
                        <p v-else class="text-gray-800" :class="jobData.work_arrangement == 'wfh' ? 'uppercase' : ''">{{ $filters.screenedWorkArrangement(jobData.work_arrangement) }}</p>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
                        <div class="mt-6" v-if="jobData.roster">
                            <span class="block text-gray-500">Roster</span>
                            <p class="text-gray-800">{{ jobData.roster }}</p>
                        </div>

                        <div class="mt-6" v-if="jobData.hours">
                            <span class="block text-gray-500">Hours</span>
                            <p class="text-gray-800">{{ jobData.hours }}</p>
                        </div>
                    </div>

                    <template v-if="jobData.is_project">
                        <div class="mt-6">
                            <span class="block text-gray-500">Project description</span>
                            <p class="text-gray-800">{{ jobData.project_description }}</p>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
                            <div class="mt-6" v-if="jobData.project_timeframe">
                                <span class="block text-gray-500">Project timeframe</span>
                                <p class="text-gray-800">{{ jobData.project_timeframe }}</p>
                            </div>

                            <div class="mt-6" v-if="jobData.project_stage">
                                <span class="block text-gray-500">Project stage</span>
                                <p class="text-gray-800">{{ jobData.project_stage }}</p>
                            </div>

                            <div class="mt-6" v-if="jobData.project_team_size">
                                <span class="block text-gray-500">Project team size</span>
                                <p class="text-gray-800">{{ jobData.project_team_size }}</p>
                            </div>

                            <div class="mt-6" v-if="jobData.project_budget">
                                <span class="block text-gray-500">Project budget</span>
                                <p class="text-gray-800">{{ jobData.project_budget }}</p>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
    <ReviseRateModal
        v-if="showRateModal && !isImported"
        :current-rate="{rate: profile.rate, type: profile.rate_type}"
        @close="showRateModal = false"
        @rate-updated="handleRateUpdate"
    />
</template>

<script>
import config from '@/config';

let apiEndpoint = process.env.VUE_APP_API_BASEURL
import contractorService from '@/services/marketplace/contractor.service';
import ReviseRateModal from '@/components/marketplace/contractor/modals/ReviseRateModal.vue';

export default {
    name: 'ViewJob',
    inject: ['toggleLoading'],
    components: {
        ReviseRateModal
    },
    data() {
        return {
            jobData: [],
            profile: [],
            hasApplied: false,
            currentTab: 'job',
            tabs: [
                { id: 'job', name: 'Job' },
                { id: 'applicants', name: 'Applicants' }
            ],
            applicants: [
                { id: 1, name: 'John Doe', email: 'john.doe@example.com', status: 'Approved' },
                { id: 2, name: 'Jane Smith', email: 'jane.smith@example.com', status: 'Pending' },
                { id: 3, name: 'Michael Brown', email: 'michael.brown@example.com', status: 'Rejected' }
            ],
            showRateModal: false,
            isMapLoading: true
        };
    },
    methods: {
        async fetchJob() {
            this.toggleLoading(true);
            try {
                contractorService.getJob(this.$route.params.jobId)
                    .then(response => {
                        this.jobData = response.data;
                        this.hasApplied = this.jobData.hasApplied

                        if (this.jobData.is_imported) {
                            document.title = this.jobData.seo?.title || `${this.jobData.title} | Screened`;
                            this.setMetaDescription(this.jobData.seo?.metaDescription || this.jobData.structured_job?.excerpt || '');
                        }

                        if (this.jobData.location_lat && this.jobData.location_lng) {
                            this.loadGoogleMapsScript()
                        }

                        this.toggleLoading(false);
                    })
                    .catch(error => {
                        this.toggleLoading(false);
                        console.error('Error fetching jobs:', error);
                    });
            } catch (error) {
                this.toggleLoading(false);
                console.error('Error fetching jobs:', error);
            }
        },
        async getContractorProfile() {
            this.toggleLoading(true);
            try {
                let response = await fetch(apiEndpoint + "/marketplace.php?getContractorAccount", {
                    headers: {
                        'Authorization': 'Bearer ' +  localStorage.getItem('contractorToken'),
                    },
                })

                if (response.ok) {
                    this.profile = await response.json();

                    this.toggleLoading(false);
                } else {
                    this.toggleLoading(false);
                    this.$router.push('/contractor/logout');
                }
            } catch (error) {
                console.log(error)
                this.toggleLoading(false);
            }
        },
        formatDate(timestamp) {
            if (!timestamp) return '';
            if (timestamp == 1) return "ASAP";
            const date = new Date(timestamp * 1000); // Convert from seconds to milliseconds
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        },
        formatCalendarDate(value) {
            if (!value) return 'Not specified';
            const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
            return Number.isNaN(date.getTime())
                ? value
                : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
        },
        setMetaDescription(content) {
            if (!content) return;
            let element = document.querySelector('meta[name="description"]');
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute('name', 'description');
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        },
        handleApplyClick() {
            // Show the rate revision modal before proceeding with application
            this.showRateModal = true
        },

        handleRateUpdate(newRate) {
            this.applyForJob(newRate)
        },

        async applyForJob(rate) {

            this.toggleLoading(true);

            contractorService.applyJob(this.jobData.uuid, rate.rate, rate.rate_type)
                .then(response => {
                    if (response.data.error) {
                        this.$root.notify(response.data.errorMessage, 'error');
                    } else {
                        this.hasApplied = true;
                    }
                })
                .catch((error) => {
                    this.$root.notify('Something went wrong while applying to the job, please try again', 'error');
                })
                .finally(() => {
                    this.toggleLoading(false);
            })
        },
        applyJob() {
            this.toggleLoading(true);

            contractorService.applyJob(this.jobData.uuid)
                .then(response => {
                    if (response.data.error) {
                        this.$root.notify(response.data.errorMessage, 'error');
                    } else {
                        this.hasApplied = true;
                    }
                })
                .catch((error) => {
                    this.$root.notify('Something went wrong while applying to the job, please try again', 'error');
                })
                .finally(() => {
                    this.toggleLoading(false);
            })
        },
        async copyJobIdToClipboard() {
            try {
                await navigator.clipboard.writeText('#' + this.jobData.slug);
                // Reset copied status after 2 seconds
            this.$root.notify('Job ID #' + this.jobData.slug + ' has been copied to your clipboard.', 'success');
            } catch (err) {
                // Don't do anything really, if it's not copied it's not copied
            }
        },
        formatDatabaseString(str) {
          if (str) {
            return str.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase());
          }
        },
        loadGoogleMapsScript() {
        const apiKey = config.googleMaps.apiKey;

        if (!apiKey) {
          console.error('Google Maps API key not configured');
          this.isMapLoading = false;
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.$nextTick(() => {
            this.initMap();
          });
        };
        document.head.appendChild(script);
      },
      initMap() {
        // Make sure the map container is rendered
        if (!this.$refs.mapContainer) {
          console.error('Map container reference not found');
          this.isMapLoading = false;
          return;
        }

        try {
          // Default coordinates (will be updated by geocoding)
          const defaultPosition = { lat: -33.867, lng: 151.205 };

          // Create the map
          this.map = new google.maps.Map(this.$refs.mapContainer, {
            zoom: 14,
            center: defaultPosition,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            zoomControlOptions: {
              position: google.maps.ControlPosition.RIGHT_TOP
            },
            styles: [
              {
              "featureType": "administrative",
              "elementType": "labels.text.fill",
              "stylers": [{"color": "#484848"}]
              },
              {
              "featureType": "landscape",
              "elementType": "all",
              "stylers": [{"color": "#f7f7f7"}]
              },
              {
              "featureType": "poi",
              "elementType": "all",
              "stylers": [{"visibility": "off"}]
              },
              {
              "featureType": "road",
              "elementType": "all",
              "stylers": [{"saturation": -100}, {"lightness": 45}]
              },
              {
              "featureType": "transit",
              "elementType": "all",
              "stylers": [{"visibility": "simplified"}]
              },
              {
              "featureType": "water",
              "elementType": "all",
              "stylers": [{"color": "#0ea5e9"}, {"visibility": "on"}, {"lightness": 70}]
              }
            ]
          });

          // Create the marker
          this.marker = new google.maps.Marker({
            position: defaultPosition,
            map: this.map,
            animation: null,
            draggable: true,
            icon: {
              path: "M 12,2 C 8.1340068,2 5,5.1340068 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.8659932 -3.134007,-7 -7,-7 z",
              fillOpacity: 1,
              fillColor: '#0ea5e9',
              scale: 1.5,
              strokeColor: '#ffffff',
              strokeWeight: 1.5,
              anchor: new google.maps.Point(12, 24),
              zIndex: 100
            }
          });

          this.isMapLoading = false
        } catch (error) {
          console.error('Error initializing map:', error);
          this.$root.notify('Error loading map. Please try again.', 'error');
          this.isMapLoading = false;
        }
      },
    },
    mounted() {
        this.fetchJob();
        this.getContractorProfile()
    },
    computed: {
        isImported() {
            return Boolean(this.jobData && this.jobData.is_imported);
        },
        importedSalary() {
            const salary = this.jobData?.structured_job?.salary || {};
            if (salary.originalText) return salary.originalText;
            if (salary.minimum == null && salary.maximum == null) return 'Not specified';

            const currency = salary.currency === 'AUD' || !salary.currency ? '$' : `${salary.currency} `;
            const minimum = salary.minimum == null ? null : `${currency}${Number(salary.minimum).toLocaleString()}`;
            const maximum = salary.maximum == null ? null : `${currency}${Number(salary.maximum).toLocaleString()}`;
            const amount = minimum && maximum && minimum !== maximum ? `${minimum}–${maximum}` : (minimum || maximum);
            return salary.period && salary.period !== 'unknown' ? `${amount} per ${salary.period}` : amount;
        },
        importedDescriptionHtml() {
            const markdown = String(this.jobData?.description_markdown || '');
            const escape = (value) => value
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
            const inline = (value) => escape(value).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
            const lines = markdown.split(/\r?\n/);
            const output = [];
            let listOpen = false;

            lines.forEach((line) => {
                if (/^-\s+/.test(line)) {
                    if (!listOpen) output.push('<ul>');
                    listOpen = true;
                    output.push(`<li>${inline(line.replace(/^-\s+/, ''))}</li>`);
                    return;
                }
                if (listOpen) {
                    output.push('</ul>');
                    listOpen = false;
                }
                if (/^###\s+/.test(line)) output.push(`<h3>${inline(line.replace(/^###\s+/, ''))}</h3>`);
                else if (/^##\s+/.test(line)) output.push(`<h2>${inline(line.replace(/^##\s+/, ''))}</h2>`);
                else if (line.trim()) output.push(`<p>${inline(line.trim())}</p>`);
            });
            if (listOpen) output.push('</ul>');
            return output.join('');
        },
        formattedPostedDate() {
            const creationDate = new Date(this.jobData.creation_date);
            if (Number.isNaN(creationDate.getTime())) return '';
            const now = new Date();
            const diffInMs = now - creationDate;
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60)); // Convert ms to hours

            if (diffInHours < 24) {
                return `Posted ${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
            }

            const diffInDays = Math.floor(diffInHours / 24);
            if (diffInDays === 1) {
                return "Posted 1 day ago";
            }

            return `Posted ${diffInDays} days ago`;
        }
    }
};
</script>

<style scoped>
.imported-job-description :deep(h2) {
    margin: 1.5rem 0 0.5rem;
    color: #111827;
    font-size: 1.25rem;
    font-weight: 700;
}
.imported-job-description :deep(h3) {
    margin: 1.25rem 0 0.5rem;
    color: #111827;
    font-size: 1.1rem;
    font-weight: 700;
}
.imported-job-description :deep(p) {
    margin: 0 0 0.85rem;
    line-height: 1.7;
}
.imported-job-description :deep(ul) {
    margin: 0 0 1rem 1.25rem;
    list-style: disc;
}
.imported-job-description :deep(li) {
    margin-bottom: 0.35rem;
    line-height: 1.6;
}
</style>
